import React, { useState } from 'react';
import { Save, X } from 'lucide-react';
import { apiClient } from '../config/api';
import { useNavigate } from 'react-router-dom';

const AddReport = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    report_title: '',
    company_name: '',
    company_ticker: '',
    activist_short_seller: '',
    report_date: '',
    report_url: '',
    report_summary: '',
    attack_type: '',
    market_cap_category: '',
    sector: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const attackTypes = [
    'Accounting Fraud',
    'Business Model Issues',
    'Management Concerns',
    'Valuation Issues',
    'Regulatory Problems',
    'Competitive Threats',
    'Financial Misstatements',
    'Other'
  ];

  const marketCapCategories = [
    'Large Cap (>$10B)',
    'Mid Cap ($2B-$10B)',
    'Small Cap ($300M-$2B)',
    'Micro Cap (<$300M)'
  ];

  const sectors = [
    'Technology',
    'Healthcare',
    'Financial Services',
    'Consumer Discretionary',
    'Energy',
    'Industrials',
    'Materials',
    'Consumer Staples',
    'Real Estate',
    'Utilities',
    'Communication Services'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.report_title || !formData.company_name || !formData.activist_short_seller || !formData.report_date) {
        throw new Error('Please fill in all required fields');
      }

      await apiClient.post('/api/short-sellers', formData);
      
      // Redirect to reports page after successful creation
      navigate('/reports');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create report');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/reports');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Add New Report</h1>
        <button
          onClick={handleCancel}
          className="btn-secondary flex items-center"
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="report_title"
                value={formData.report_title}
                onChange={handleInputChange}
                placeholder="Enter report title..."
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder="Enter company name..."
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Ticker
              </label>
              <input
                type="text"
                name="company_ticker"
                value={formData.company_ticker}
                onChange={handleInputChange}
                placeholder="e.g., TSLA, AAPL"
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activist Short Seller <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="activist_short_seller"
                value={formData.activist_short_seller}
                onChange={handleInputChange}
                placeholder="e.g., Hindenburg Research"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="report_date"
                value={formData.report_date}
                onChange={handleInputChange}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report URL
              </label>
              <input
                type="url"
                name="report_url"
                value={formData.report_url}
                onChange={handleInputChange}
                placeholder="https://..."
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attack Type
              </label>
              <select
                name="attack_type"
                value={formData.attack_type}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="">Select attack type...</option>
                {attackTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Market Cap Category
              </label>
              <select
                name="market_cap_category"
                value={formData.market_cap_category}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="">Select category...</option>
                {marketCapCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sector
              </label>
              <select
                name="sector"
                value={formData.sector}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="">Select sector...</option>
                {sectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Summary
            </label>
            <textarea
              name="report_summary"
              value={formData.report_summary}
              onChange={handleInputChange}
              placeholder="Enter a brief summary of the report..."
              rows={4}
              className="input-field"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Report
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddReport; 